import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

@Component({
  selector: 'so-drop-simple-file-ui',
  standalone: true,
  imports: [CommonModule, SoSvgIconUI, SoButtonGradientUI],
  templateUrl: './so-drop-simple-file.component.html',
  styleUrl: './so-drop-simple-file.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoDropSimpleFileUI {
  @ViewChild('changeInputRef') changeInputRef!: ElementRef<HTMLInputElement>;

  imageHeight = input<string>('60px');
  imageWidth = input<string>('60px');
  inputHeight = input<string>('60px');
  inputWidth = input<string>('60px');
  imageBorderRadius = input<string>('var(--so-radius-btn, 8px)');
  label = input<string>('');
  maxSizeFile = input<number | null>(null);
  imageSrc = input<string | ArrayBuffer | null | undefined>(null);
  confirmDelete = input<boolean>(false);
  idInptut = input.required<string>();
  idChangeInput = input.required<string>();

  isSizeValid = output<boolean>();
  fileSelected = output<File>();
  removeFile = output<boolean>();
  removeRequested = output<void>();

  protected internalSrc = signal<string | ArrayBuffer | null>(null);
  protected cleared = signal<boolean>(false);

  protected displaySrc = computed(() => {
    if (this.cleared()) return null;
    return this.internalSrc() ?? (this.imageSrc() as string | ArrayBuffer | null) ?? null;
  });

  constructor() {
    effect(() => {
      const src = this.imageSrc();
      if (!src) {
        this.internalSrc.set(null);
      } else {
        this.cleared.set(false);
      }
    });
  }

  private readonly allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;
    const file = input.files[0];

    if (!this.allowedTypes.includes(file.type)) {
      input.value = '';
      return;
    }

    const maxSize = this.maxSizeFile();
    if (maxSize !== null) {
      if (file.size > maxSize * 1024 * 1024) {
        this.isSizeValid.emit(false);
        input.value = '';
        return;
      }
      this.isSizeValid.emit(true);
    }

    this.cleared.set(false);
    this.fileSelected.emit(file);
    const reader = new FileReader();
    reader.onload = () => {
      this.internalSrc.set(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  clearImage(): void {
    this.cleared.set(true);
    this.internalSrc.set(null);
    const el = document.getElementById(this.idInptut()) as HTMLInputElement;
    if (el) el.value = '';
    if (this.changeInputRef?.nativeElement) this.changeInputRef.nativeElement.value = '';
    this.removeFile.emit(true);
  }

  onRemoveClick(): void {
    this.removeRequested.emit();
  }

  triggerFileInput(): void {
    this.changeInputRef?.nativeElement.click();
  }
}
